import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, KpiCard, Spinner,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import { useAuth } from '@kss/auth';
import {
  CheckCircle2, KeyRound, RefreshCw, Search, ShieldCheck, UserCheck,
} from 'lucide-react';

/**
 * "My permissions" — what the signed-in user can actually do.
 *
 * Resolves the union of:
 *   - direct grants  (UserPermission)
 *   - role grants    (UserRole → RolePermission)
 * and lays them out by module group so the user can answer "why am I
 * seeing this menu / why did that action fail with 403?" without
 * pinging an admin.
 *
 * Data sources (backend §11/§12):
 *   GET /api/security/Permission                — every permission row
 *   GET /api/security/UserPermission?userId=me  — direct grants
 *   GET /api/security/UserRole?userId=me        — assigned roles
 *   GET /api/security/RolePermission            — role → permission map
 *   GET /api/security/Role                      — role display names
 *
 * Falls back gracefully when any of these are missing — the page never
 * blank-screens, it just downgrades from "by source" to "by category".
 */

interface PermissionRow {
  permissionId?: string;
  id?: number | string;
  code?: string;
  name?: string;
  description?: string;
  category?: string;
}

interface UserPermissionRow {
  userPermissionId?: string;
  permissionId?: string;
  permissionCode?: string;
  userId?: string;
}

interface UserRoleRow {
  userRoleId?: string;
  userId?: string;
  roleId?: string;
  roleName?: string;
}

interface RolePermissionRow {
  rolePermissionId?: string;
  roleId?: string;
  permissionId?: string;
  permissionCode?: string;
}

interface RoleRow {
  roleId?: string;
  id?: number | string;
  name?: string;
  code?: string;
}

interface Resolved {
  code: string;
  name: string;
  category: string;
  /** Which sources granted this — used for the "Direct + Role(Admin)" pill. */
  sources: string[];
}

export default function MyPermissionsPage() {
  const me = String(useAuth().user?.id ?? '');
  const [perms, setPerms] = useState<PermissionRow[]>([]);
  const [userPerms, setUserPerms] = useState<UserPermissionRow[]>([]);
  const [userRoles, setUserRoles] = useState<UserRoleRow[]>([]);
  const [rolePerms, setRolePerms] = useState<RolePermissionRow[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      // Live endpoints live under /authorization (the /security/* paths 404).
      // UserPermission/UserRole are filtered to the signed-in user by real id
      // (EntityQueryFilter honours ?userId=<id>; the old literal "me" matched nothing).
      const uq = me ? `?userId=${encodeURIComponent(me)}` : '';
      const [pAll, upMine, urMine, rpAll, rAll] = await Promise.all([
        apiClient.get('/authorization/Permission').then(unwrap<PermissionRow[]>).catch(() => []),
        apiClient.get(`/authorization/UserPermission${uq}`).then(unwrap<UserPermissionRow[]>).catch(() => []),
        apiClient.get(`/authorization/UserRole${uq}`).then(unwrap<UserRoleRow[]>).catch(() => []),
        apiClient.get('/authorization/RolePermission').then(unwrap<RolePermissionRow[]>).catch(() => []),
        apiClient.get('/authorization/Role').then(unwrap<RoleRow[]>).catch(() => []),
      ]);
      setPerms(pAll);
      setUserPerms(upMine);
      setUserRoles(urMine);
      setRolePerms(rpAll);
      setRoles(rAll);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [me]);

  const resolved = useMemo<Resolved[]>(() => {
    const byId = new Map<string, PermissionRow>();
    const byCode = new Map<string, PermissionRow>();
    for (const p of perms) {
      const id = String(p.permissionId ?? p.id ?? '');
      const code = String(p.code ?? '');
      if (id) byId.set(id, p);
      if (code) byCode.set(code, p);
    }
    const roleNameById = new Map<string, string>();
    for (const r of roles) {
      const id = String(r.roleId ?? r.id ?? '');
      if (id) roleNameById.set(id, String(r.name ?? r.code ?? id.slice(0, 6)));
    }
    const myRoleIds = new Set(userRoles.map((u) => String(u.roleId ?? '')).filter(Boolean));

    // Aggregate by permission code.
    const grants = new Map<string, Resolved>();

    function ensure(code: string): Resolved {
      const existing = grants.get(code);
      if (existing) return existing;
      const meta = byCode.get(code);
      const r: Resolved = {
        code,
        name: String(meta?.name ?? code),
        category: String(meta?.category ?? deriveCategory(code)),
        sources: [],
      };
      grants.set(code, r);
      return r;
    }

    // Direct user grants
    for (const up of userPerms) {
      const code = String(up.permissionCode ?? byId.get(String(up.permissionId ?? ''))?.code ?? '');
      if (!code) continue;
      ensure(code).sources.push('Direct');
    }

    // Role grants
    for (const rp of rolePerms) {
      const roleId = String(rp.roleId ?? '');
      if (!myRoleIds.has(roleId)) continue;
      const code = String(rp.permissionCode ?? byId.get(String(rp.permissionId ?? ''))?.code ?? '');
      if (!code) continue;
      const roleName = roleNameById.get(roleId) ?? roleId.slice(0, 6);
      ensure(code).sources.push(`Role(${roleName})`);
    }

    return Array.from(grants.values()).sort((a, b) => {
      if (a.category === b.category) return a.code.localeCompare(b.code);
      return a.category.localeCompare(b.category);
    });
  }, [perms, userPerms, userRoles, rolePerms, roles]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const m: Record<string, Resolved[]> = {};
    for (const r of resolved) {
      if (q && !`${r.code} ${r.name}`.toLowerCase().includes(q)) continue;
      (m[r.category] ??= []).push(r);
    }
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [resolved, search]);

  const myRoleNames = useMemo(() => {
    const byId = new Map<string, string>();
    for (const r of roles) {
      const id = String(r.roleId ?? r.id ?? '');
      if (id) byId.set(id, String(r.name ?? r.code ?? id.slice(0, 6)));
    }
    return userRoles
      .map((u) => byId.get(String(u.roleId ?? '')) ?? u.roleName)
      .filter((s): s is string => !!s);
  }, [userRoles, roles]);

  const directCount = resolved.filter((r) => r.sources.some((s) => s === 'Direct')).length;
  const roleCount = resolved.filter((r) => r.sources.some((s) => s.startsWith('Role('))).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">My permissions</h2>
          <p className="text-xs text-content-secondary">
            What you can do across {groups.length} module(s) · resolved from your roles + any direct grants.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
      </div>

      {err && <Alert variant="warning">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Total"
          value={resolved.length.toLocaleString()}
          subtitle="Permissions in effect"
          icon={<ShieldCheck className="h-5 w-5 text-success" />}
        />
        <KpiCard
          title="From roles"
          value={roleCount.toLocaleString()}
          subtitle={myRoleNames.length ? myRoleNames.join(', ') : 'No roles assigned'}
          icon={<UserCheck className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Direct grants"
          value={directCount.toLocaleString()}
          subtitle="UserPermission rows"
          icon={<KeyRound className="h-5 w-5 text-primary" />}
        />
        <KpiCard
          title="Module groups"
          value={groups.length.toLocaleString()}
          subtitle="Categories you can see"
          icon={<CheckCircle2 className="h-5 w-5 text-success" />}
        />
      </div>

      <Card>
        <div className="mb-3 flex items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-content-muted" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by code or name…"
              className="w-full rounded-md border border-border bg-surface-card py-1.5 pl-7 pr-2 text-sm outline-none focus:border-primary"
            />
          </div>
        </div>

        {loading && resolved.length === 0 ? (
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
            <Spinner /> Loading permissions…
          </div>
        ) : groups.length === 0 ? (
          <div className="p-6 text-sm text-content-muted">
            {resolved.length === 0
              ? 'No permissions resolved. If this is unexpected, ask your administrator to re-run “Grant admin everything”.'
              : 'No permissions match the current filter.'}
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map(([category, items]) => (
              <section key={category}>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-muted">
                  {category} · {items.length}
                </h3>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((p) => (
                    <div
                      key={p.code}
                      className="flex items-start gap-2 rounded-md border border-border bg-surface-card px-3 py-2 text-xs"
                    >
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-success" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-content-primary">{p.name}</div>
                        <div className="truncate text-[11px] text-content-muted">{p.code}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {dedup(p.sources).map((s) => (
                            <SourcePill key={s} v={s} />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function SourcePill({ v }: { v: string }) {
  let cls = 'border-info/40 bg-info/10 text-info';
  if (v === 'Direct') cls = 'border-primary/40 bg-primary/10 text-primary';
  return (
    <span className={`inline-flex rounded-full border px-1.5 py-0 text-[10px] ${cls}`}>{v}</span>
  );
}

function dedup<T>(xs: T[]): T[] {
  return Array.from(new Set(xs));
}

function deriveCategory(code: string): string {
  // M-SALES-INV → "Sales", pos.charge → "Pos", accounting.post → "Accounting"
  if (code.startsWith('M-')) {
    const seg = code.split('-')[1] ?? '';
    return titleCase(seg);
  }
  const dot = code.indexOf('.');
  if (dot > 0) return titleCase(code.slice(0, dot));
  return 'Other';
}

function titleCase(s: string): string {
  if (!s) return 'Other';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function unwrap<T>(r: { data: unknown }): T {
  const d = r.data;
  if (d && typeof d === 'object' && 'data' in (d as Record<string, unknown>)) {
    return (d as { data: T }).data;
  }
  if (d && typeof d === 'object' && 'items' in (d as Record<string, unknown>)) {
    return (d as { items: T }).items;
  }
  return d as T;
}
